import { useNavigate } from 'react-router-dom';
import { useContext, useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { LoginContext } from '../../Contexts/LoginContext';
import { formatCurrency } from '../../data/constants';
import { getBrowserLocation } from '../../utils/buyerLocation';
import { formatCityRegion } from '../utils/formatAddress';

const getDistanceKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const b2bCompanyAvatar = (name) => {
  const n = encodeURIComponent((name || 'Supplier').slice(0, 40));
  return `https://ui-avatars.com/api/?size=128&background=f5f0eb&color=6b4423&name=${n}`;
};


const ProductCard = ({ product, b2bListing, linkToSellerView }) => {
  const navigate = useNavigate();
  const { currency, exchangeRate, user } = useContext(LoginContext);
  const isB2B = Boolean(b2bListing);
  const [browserLoc, setBrowserLoc] = useState(null);

  // Try browser geolocation when user location is not set
  const scope = isB2B ? b2bListing?.sellingScope : product?.sellingScope;
  const sellerPoint = isB2B ? b2bListing?.b2bSellerId?.sellerId?.location : product?.sellerDetails?.location;

  useEffect(() => {
    if (scope !== 'local') return;
    const buyerLoc = user?.location;
    if (buyerLoc && buyerLoc.lat !== 0 && buyerLoc.lon !== 0) return; // already have location
    getBrowserLocation().then((loc) => {
      if (loc) setBrowserLoc(loc);
    });
  }, [scope, user?.location]);

  const handleCardClick = () => {
    if (isB2B) {
      const isSellerViewingOwn = linkToSellerView && user?.role === 'seller';
      const path = isSellerViewingOwn
        ? `/seller/productDetail/${b2bListing._id}?market=b2b`
        : `/productDetail/${b2bListing._id}?market=b2b`;
      navigate(path);
      return;
    }
    const isSellerViewingOwn = linkToSellerView && user?.role === 'seller';
    if (!isSellerViewingOwn) {
      axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/products/product/${product.id}/click`).catch(() => {});
    }
    const path = isSellerViewingOwn ? `/seller/productDetail/${product.id}` : `/productDetail/${product.id}`;
    navigate(path, { state: { product } });
  };

  const distanceKm = useMemo(() => {
    // Only show distance for local listings
    if (scope !== 'local') return null;
    if (product?.distanceKm != null) return product.distanceKm < 1 ? '< 1' : product.distanceKm.toLocaleString();
    const sellerLoc = sellerPoint;
    const buyerLoc = (user?.location && user.location.lat !== 0 && user.location.lon !== 0)
      ? user.location
      : browserLoc;
    if (
      !sellerLoc || !buyerLoc ||
      (sellerLoc.lat === 0 && sellerLoc.lon === 0)
    ) {
      return null;
    }
    const d = getDistanceKm(buyerLoc.lat, buyerLoc.lon, sellerLoc.lat, sellerLoc.lon);
    return d < 1 ? '< 1' : Math.round(d).toLocaleString();
  }, [scope, product?.distanceKm, sellerPoint, user?.location, browserLoc]);

  if (isB2B) {
    const listing = b2bListing;
    const lowestPrice = listing.priceTiers?.length > 0 ? Math.min(...listing.priceTiers.map((t) => t.price)) : 0;
    const cover = listing.images?.length ? listing.images[0] : listing.thumbnail;
    const company = listing.b2bSellerId?.companyName || 'Supplier';
    const avatar = listing.b2bSellerId?.sellerId?.profileImage || b2bCompanyAvatar(company);
    const country = formatCityRegion(listing.b2bSellerId?.businessAddress)
      || formatCityRegion(listing.b2bSellerId?.sellerId?.sellerAddress)
      || listing.sellerCountry || '';
    const mode = listing.listingMode;

    return (
      <div className="productCard" onClick={handleCardClick}>
        <div className="productImage">
          <img src={cover} alt={listing.title} />
          {mode === 'customization' && <div className="productCardBadge worldwideBadge" style={{ background: '#5b21b6' }}>Custom</div>}
          <div className="topLeftBadges">
            {listing.listingType === 'service' && <div className="serviceTag" title="This listing is a service">SERVICE</div>}
            {listing.boostedUntil && new Date(listing.boostedUntil) > new Date() && (
              <div className="featuredTag" title="Promoted listing">FEATURED</div>
            )}
            {mode === 'both' && <div className="bulkTag">Wholesale + Custom</div>}
            {mode === 'wholesale' && <div className="bulkTag">Wholesale</div>}
          </div>
          {distanceKm !== null && (
            <div className="distanceTag">📍 {distanceKm} km</div>
          )}
        </div>
        <div className="productDetails">
          <div className="profile">
            <img className="profileImage" src={avatar} alt="" />
            <div className="profileInfo">
              <p className="name">{company}</p>
              <p className="location">{country || '—'}</p>
            </div>
            <div className="ratingReviewContainer">
              <span className="rating">{(listing.rating ?? 0).toFixed(1)}</span>
              <span className="reviews">({listing.noOfReviews ?? 0})</span>
              <span className="star" style={{ marginLeft: '3px' }}>★</span>
            </div>
          </div>
          <p className="doubleLineSmallText productDescription">{listing.title}</p>
          <div className="price">
            {lowestPrice > 0 ? (
              <span className="discountedPrice">
                From {formatCurrency(lowestPrice, currency, exchangeRate)}<span style={{ fontWeight: 500, fontSize: '0.9em' }}>/Unit</span>
              </span>
            ) : (
              <span className="discountedPrice contactForPricing">Contact for Pricing</span>
            )}
            <span className="bulkPriceHint">MOQ {listing.moq}{listing.unitsSold > 0 ? ` · ${listing.unitsSold} sold` : ''}</span>
          </div>
        </div>
      </div>
    );
  }

  const isService = product.listingType === 'service';
  // A service has no stock to run out of.
  const isOutOfStock = !isService && product.stock != null && product.stock <= 0;

  return (
    <div className={`productCard ${isOutOfStock ? 'outOfStock' : ''}`} onClick={handleCardClick}>
      <div className="productImage">
        <img src={product.images[0]} alt="Product" />
        {product.sellingScope === 'international' && (
          <div className="productCardBadge worldwideBadge" title="Worldwide shipping available">🌍 International</div>
        )}
        {product.sellingScope === 'local' && (
          <div className="productCardBadge localBadge" title="Local listing">📍 Local</div>
        )}
        <div className="topLeftBadges">
          {/* A service is booked rather than shipped, so it is labelled before
              the buyer opens it — the detail page has no Add to Cart for one. */}
          {isService && <div className="serviceTag" title="This listing is a service">SERVICE</div>}
          {/* Paid placement is labelled rather than hidden — consumer
              protection rules in most of our markets require promoted results
              to be identifiable as such. */}
          {product.isBoosted && <div className="featuredTag" title="Promoted listing">FEATURED</div>}
          {product.isDiscounted && <div className="saleTag">{product.discountLabel || 'SALE'}</div>}
          {product.hasBulkPricing && (
            <div className="bulkTag">BULK DEALS</div>
          )}
        </div>
        {distanceKm !== null && (
          <div className="distanceTag">📍 {distanceKm} km</div>
        )}
        {isOutOfStock && <div className="outOfStockOverlay">Out of Stock</div>}
      </div>
      <div className="productDetails">
        <div className="profile">
          <img className="profileImage" src={product.sellerDetails.profileImageUrl} alt={product.name} />
          <div className="profileInfo">
            <p className="name">{product.sellerDetails.displayName}</p>
            <p className="location">{product.sellerDetails.sellerAddress?.city}, {product.sellerDetails.sellerAddress?.country.name}</p>
          </div>
          <div className="ratingReviewContainer">
            <span className="rating">{product.rating.toFixed(1)}</span>
            <span className="reviews">({product.reviews})</span>
            <span className="star" style={{ marginLeft: "3px" }}>★</span>
          </div>
        </div>
        <p className="doubleLineSmallText productDescription">{product.title}</p>
        <div className="price">
          {product.isDiscounted ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="discountedPrice">{formatCurrency(product.price, currency, exchangeRate)}</span>
              <span className="originalPrice">{formatCurrency(product.originalPrice, currency, exchangeRate)}</span>
            </div>
          ) : (
            <span className="discountedPrice">From {formatCurrency(product.price, currency, exchangeRate)}</span>
          )}
          {product.hasBulkPricing && product.lowestBulkPrice && (
            <span className="bulkPriceHint">
              As low as {formatCurrency(product.lowestBulkPrice, currency, exchangeRate)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
